import {Subject, combineLatest, merge, Observable} from 'rxjs';
import {filter, map, take, takeUntil, tap} from 'rxjs/operators';
import {klineUtil} from '@/helper/kline';
import {tradePair$} from '@/state/trade-pair';
import {toWsMsg$} from '@/ws';
import {toUiMsg$} from '@/ui';
import {klineMsg$} from '@/raw/kline';
import type {ws, KlineBar} from '@/types';

export interface UKline {
  type: 'kline';
  data: KlineData;
}

type KlineMsg = ws.KlineMsg;
type KlineItemRaw = ws.KlineItemRaw;

interface KlineData {
  type: ws.MsgType;
  symbol: string;
  resolution: number;
  bars: KlineBar[];
}

/**
 * K线信道，聚合K线数据
 */
export class KlineChannel {
  private dispose$: Subject<void>;
  private readonly window: number;
  private snapshotLastTime: number;
  private lastState: {id: number; buffer: any[]};
  private snapshotData$: Observable<KlineData>;
  private incrementalData$: Observable<KlineData>;

  constructor(
    private readonly symbol,
    private readonly resolution,
    private readonly period,
    private readonly tradeType
  ) {
    this.symbol = symbol;
    this.tradeType = tradeType;
    this.resolution = resolution; // 单位是分钟
    this.period = period; // 实际周期，单位是秒
    this.window = this.resolution * 60000;
    this.snapshotLastTime = 0; // 最新一条全量数据的时间，用于移除实时数据中与历史数据重叠的数据
    this.lastState = {id: 0, buffer: []};
    this.init();
  }

  init() {
    const {symbol, resolution, period} = this;
    this.dispose$ = new Subject<void>(); // 销毁事件控制流
    const klineData$ = klineMsg$.pipe(
      filter((o: KlineMsg) => o.kline.length > 0 && o.symbol === symbol && o.kline[0][1] === period)
    );

    const snapshotBars$ = klineData$.pipe(
      filter((msg: KlineMsg) => msg.type === 'snapshot'),
      tap((msg: KlineMsg) => (this.snapshotLastTime = msg.kline[0][0])),
      map((msg: KlineMsg): KlineItemRaw[] => msg.kline.reverse()),
      take(1)
    );

    this.snapshotData$ = combineLatest([snapshotBars$, tradePair$]).pipe(
      map(([snapshotBars, tradePair]) =>
        snapshotBars.map((bar: KlineItemRaw) => klineUtil.convertToBar(bar, tradePair))
      ),
      map((bars: KlineBar[]): KlineData => ({type: 'snapshot', symbol, resolution, bars}))
    );

    const incrementalBars$ = klineData$.pipe(
      filter((msg: KlineMsg) => msg.type === 'incremental'),
      map((msg: KlineMsg): KlineItemRaw[] => msg.kline.filter((bar) => bar[0] >= this.snapshotLastTime).reverse())
    );

    this.incrementalData$ = combineLatest([incrementalBars$, tradePair$]).pipe(
      map(([incrementalBars, tradePair]) =>
        incrementalBars.map((bar: KlineItemRaw) => klineUtil.convertToBar(bar, tradePair))
      ),
      map((bars: KlineBar[]): KlineData => ({type: 'incremental', symbol, resolution, bars}))
    );
  }

  start() {
    merge<KlineData[]>(this.snapshotData$, this.incrementalData$)
      .pipe(takeUntil(this.dispose$))
      .subscribe((data) => toUiMsg$.next({type: 'kline', data}));
    this.subscribeKline();
  }

  dispose(shouldNotUnsubscribe: boolean) {
    this.dispose$.next();
    this.unsubscribeKline(shouldNotUnsubscribe);
  }

  subscribeKline() {
    const method = this.tradeType === 'PerpetualV2' ? 'kline_p1.subscribe' : 'kline_v1.subscribe';
    toWsMsg$.next({method, params: [this.symbol, this.period]});
  }

  unsubscribeKline(shouldNotUnsubscribe: boolean) {
    if (shouldNotUnsubscribe) {
      return;
    }
    const method = this.tradeType === 'PerpetualV2' ? 'kline_p1.unsubscribe' : 'kline_v1.unsubscribe';
    toWsMsg$.next({method, params: [this.symbol, this.period]});
  }
}
